// Shared table shell — CTSK-466 (PatsPanel + ExternalClientsNest table
// parity). A thin presentational wrapper that renders the <table> +
// <thead> from a column config, wiring each sortable column through the
// existing SortableTh primitive (consumed as-is per the ticket's
// non-goals). Row rendering stays with the consumer — the two tables'
// row shapes are deliberately different (PATs serve self-minting,
// access-request rows serve the approval workflow; only the visual
// language is aligned).
//
// URL persistence: `useUrlTableSort` composes lib/sort.ts's
// useTableSort with a `?sort=<col>&dir=<asc|desc>` mirror, following
// the same replaceState convention as lib/use-list-search.ts's
// urlParam option (seed once on mount; write on every change; other
// params on the page — Settings' ?section=, ECN's ?tab= — are
// preserved because we only touch our own keys).

import type { ReactNode } from 'react';
import { SortableTh } from '../SortableTh.js';
import {
  useTableSort,
  type Accessors,
  type SortState,
} from '../../lib/sort.js';

export interface RowTableColumn {
  /** Sort key — must match an accessor key when sortable. */
  key: string;
  label: string;
  /** Default true. Actions-style columns pass false. */
  sortable?: boolean;
  /** Applied to the <th> (e.g. ECN's col--kind width caps). */
  headerClassName?: string;
  align?: 'left' | 'right';
}

/** Seed a SortState from the current URL's ?sort=&dir= pair. Unknown
 *  sort keys are harmless — useTableSort ignores keys with no accessor
 *  (same junk-tolerance stance as ECN's coerceTab, CTSK-357). */
export function readUrlSort(): SortState | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const key = sp.get('sort');
  if (!key) return null;
  return { key, dir: sp.get('dir') === 'desc' ? 'desc' : 'asc' };
}

/** Mirror a SortState to ?sort=&dir= (replaceState — no history spam,
 *  same as WorkItemsList's filter mirror). Null clears both params. */
export function writeUrlSort(next: SortState | null): void {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (next) {
    sp.set('sort', next.key);
    sp.set('dir', next.dir);
  } else {
    sp.delete('sort');
    sp.delete('dir');
  }
  const qs = sp.toString();
  window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
}

/** useTableSort + URL round-trip. Initial state seeds from the URL;
 *  every header click writes back. */
export function useUrlTableSort<T>(
  rows: T[] | undefined | null,
  accessors: Accessors<T>,
) {
  const { sorted, sort, setSort } = useTableSort(rows, accessors, readUrlSort() ?? undefined);
  const onSortChange = (next: SortState | null) => {
    setSort(next);
    writeUrlSort(next);
  };
  return { sorted, sort, onSortChange };
}

/** Outlined pill-style action button shape shared by both tables'
 *  Actions cells — lifted from PatsPanel's actionButtonBaseStyle
 *  (CTSK-204 sizing: 4px/10px padding, 6px radius) so ECN's buttons
 *  pick up the identical treatment (slice 6 of CTSK-466). */
export const rowActionButtonStyle = {
  display: 'inline-flex' as const,
  alignItems: 'center' as const,
  gap: 4,
  padding: '4px 10px',
  fontSize: 12,
  borderRadius: 6,
  cursor: 'pointer' as const,
};

export function RowTable({
  columns,
  sort,
  onSortChange,
  className = 'grid',
  children,
}: {
  columns: readonly RowTableColumn[];
  sort: SortState | null;
  onSortChange: (next: SortState | null) => void;
  /** Defaults to the app-wide .grid table treatment (PatsPanel's). */
  className?: string;
  /** The <tbody> rows. */
  children: ReactNode;
}) {
  return (
    <table className={className}>
      <thead>
        <tr>
          {columns.map((c) => {
            const style = c.align === 'right' ? { textAlign: 'right' as const } : undefined;
            return c.sortable === false ? (
              <th key={c.key} className={c.headerClassName} style={style}>
                {c.label}
              </th>
            ) : (
              <SortableTh
                key={c.key}
                sortKey={c.key}
                sort={sort}
                onSortChange={onSortChange}
                className={c.headerClassName}
                style={style}
              >
                {c.label}
              </SortableTh>
            );
          })}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
